/*
 * Dashboard.jsx
 *
 * Root component. Owns global state: fleet scores, alerts, blocklist, selected
 * charger, active filter, dark mode, and auto-refresh. Orchestrates the 60-second
 * polling loop. Renders the top-level layout — delegates map, alerts tab, and
 * detail panel to their own components.
 *
 * File map:
 *   utils.js        — pure helpers: tier config, diagnosis, timestamps, filters
 *   components.jsx  — UI atoms: Pill, StatCard, ChargerDot, AlertRow, ChartModal
 *   cards.jsx       — detail panel cards (CapacityCard, WorkOrderCard, etc.)
 *   charts.jsx      — Recharts wrappers (HealthChart, UsageChart, TelemetryChart)
 *   DetailPanel.jsx — charger detail side panel + MuteModal, AckModal, ScheduleWOModal
 *   MapTab.jsx      — Leaflet map with marker clustering
 *   AlertsTab.jsx   — alerts management (live, rules, history)
 *   SignInPage.jsx  — EVCS-branded landing page for signed-out users
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, useUser, SignedIn, SignedOut, UserButton } from "@clerk/clerk-react";
import {
  API, TIER_CONFIG, getTier,
  DERATING_STYLE, FLAP_STYLE,
  timeAgo, fmtAlertDate, _parseTs,
} from "./utils";
import { SubLabel, StatCard, ChargerDot } from "./components";
import { DetailPanel, MuteModal, AckModal, ScheduleWOModal } from "./DetailPanel";
import MapTab from "./MapTab";
import AlertsTab from "./AlertsTab";
import SignInPage from "./SignInPage";

export default function Dashboard() {
  const { getToken } = useAuth();
  const { user }     = useUser();

  const [topTab,      setTopTab]      = useState("FLEET");
  const [fleet,       setFleet]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [selected,    setSelected]    = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filter,      setFilter]      = useState("ALL");
  const [search,      setSearch]      = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [alerts,      setAlerts]      = useState([]);
  const [blocklist,   setBlocklist]   = useState([]);
  const [alertFilter, setAlertFilter] = useState("ALL");
  const [alertSort,   setAlertSort]   = useState("PRIORITY");
  const [muteTarget,     setMuteTarget]     = useState(null);
  const [ackTarget,      setAckTarget]      = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [blocklistOpen, setBlocklistOpen] = useState(false);
  const [toast,         setToast]         = useState(null);
  const [darkMode,      setDarkMode]      = useState(() => {
    try { return localStorage.getItem("dp-theme") === "dark"; } catch { return false; }
  });
  const mapRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", darkMode ? "dark" : "light");
    try { localStorage.setItem("dp-theme", darkMode ? "dark" : "light"); } catch { }
  }, [darkMode]);

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const authHeaders = useCallback(async () => {
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const fetchFleet = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/score/fleet`, { method: "POST", headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFleet(data);
      setLastUpdated(new Date());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const fetchAlerts = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/alerts`, { headers });
      if (res.ok) setAlerts((await res.json()).alerts || []);
    } catch { }
  }, [authHeaders]);

  const fetchBlocklist = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`${API}/blocklist`, { headers });
      if (res.ok) setBlocklist((await res.json()).blocked || []);
    } catch { }
  }, [authHeaders]);

  useEffect(() => { fetchFleet(); fetchAlerts(); fetchBlocklist(); }, [fetchFleet, fetchAlerts, fetchBlocklist]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(() => { fetchFleet(); fetchAlerts(); }, 60000);
    return () => clearInterval(id);
  }, [autoRefresh, fetchFleet, fetchAlerts]);

  const showSelectedOnMap = useCallback(() => {
    if (!selected?.latitude || !selected?.longitude) return;
    mapRef.current?.flyToCharger(selected.charger_sn, selected.latitude, selected.longitude);
  }, [selected]);

  useEffect(() => {
    if (!selected?.latitude || !selected?.longitude) return;
    mapRef.current?.flyToCharger(selected.charger_sn, selected.latitude, selected.longitude);
  }, [selected]);

  // ── Derived fleet lists ───────────────────────────────────────────────────

  const fleetResults     = fleet?.results || [];
  const fleetP1          = fleetResults.filter(c => c.priority_tier?.startsWith("P1") && !c.is_muted);
  const fleetP2          = fleetResults.filter(c => c.priority_tier?.startsWith("P2") && !c.is_muted);
  const fleetP3          = fleetResults.filter(c => c.priority_tier?.startsWith("P3") && !c.is_muted);
  const earlyWarning        = fleetResults.filter(c => c.alert_type === "EARLY_WARNING" && !c.is_muted);
  const deratingChargers    = fleetResults.filter(c => c.derating_alert && !c.is_muted);
  const silentRiskChargers  = fleetResults.filter(c => (c.silent_risk_score ?? 0) >= 0.40 && (c.fault_probability ?? 1) < 0.40 && !c.is_muted);
  const proactiveChargers   = fleetResults.filter(c => (c.bilstm_parts_prob ?? 0) >= 0.50 && !c.is_muted);
  const flappingChargers    = fleetResults.filter(c => c.is_flapping && !c.is_muted);
  const mutedChargers       = fleetResults.filter(c => c.is_muted);

  const bilstmScoredAt = fleetResults.reduce((latest, c) => {
    if (!c.bilstm_scored_at) return latest;
    return (!latest || c.bilstm_scored_at > latest) ? c.bilstm_scored_at : latest;
  }, null);
  const chargersWithoutBilstm = fleetResults.filter(c => c.bilstm_parts_prob == null).length;

  const displayedDots = fleetResults.filter(c => {
    let matchFilter;
    if      (filter === "ALL")                   matchFilter = true;
    else if (filter === "MUTED")                 matchFilter = !!c.is_muted;
    else if (filter === "DERATING")              matchFilter = !!c.derating_alert && !c.is_muted;
    else if (filter === "EW")                    matchFilter = c.alert_type === "EARLY_WARNING" && !c.is_muted;
    else if (filter === "SILENT_RISK")           matchFilter = (c.silent_risk_score ?? 0) >= 0.40 && (c.fault_probability ?? 1) < 0.40 && !c.is_muted;
    else if (filter === "PROACTIVE_MAINTENANCE") matchFilter = (c.bilstm_parts_prob ?? 0) >= 0.50 && !c.is_muted;
    else if (filter === "FLAPPING")              matchFilter = !!c.is_flapping && !c.is_muted;
    else                                         matchFilter = c.priority_tier?.startsWith(filter) && !c.is_muted;
    const q = search.toLowerCase();
    const matchSearch = !q ||
      c.charger_sn.toLowerCase().includes(q) ||
      (c.station_id && c.station_id !== "—" && c.station_id.toLowerCase().includes(q)) ||
      (c.site_name && c.site_name.toLowerCase().includes(q));
    return matchFilter && matchSearch;
  });

  // ── Alert feed derived state ──────────────────────────────────────────────

  const activeAlerts   = alerts.filter(a => a.status === "active");
  const ackedAlerts    = alerts.filter(a => a.status === "acknowledged");
  const resolvedAlerts = alerts.filter(a => a.status === "resolved");

  const fleetP1Sns = new Set(fleetP1.map(c => c.charger_sn));
  const fleetP2Sns = new Set(fleetP2.map(c => c.charger_sn));
  const fleetP3Sns = new Set(fleetP3.map(c => c.charger_sn));

  const byTimeAlert   = (a, b) => _parseTs(b.triggered_at) - _parseTs(a.triggered_at);
  const byKwhAlert    = (a, b) => {
    const aK = fleetResults.find(r => r.charger_sn === a.charger_sn)?.avg_kwh_day_30d ?? 0;
    const bK = fleetResults.find(r => r.charger_sn === b.charger_sn)?.avg_kwh_day_30d ?? 0;
    return bK - aK;
  };
  const byRecommended = (a, b) => {
    const aR = fleetResults.find(r => r.charger_sn === a.charger_sn);
    const bR = fleetResults.find(r => r.charger_sn === b.charger_sn);
    const score = r => ((r?.corroboration_score ?? 0) / 7) * (r?.fault_probability ?? 0) * Math.log1p(r?.avg_kwh_day_30d ?? 0);
    return score(bR) - score(aR);
  };
  const alertSortFn  = alertSort === "THROUGHPUT" ? byKwhAlert : alertSort === "RECOMMENDED" ? byRecommended : byTimeAlert;
  const sortedActive = [...activeAlerts].sort(alertSortFn);

  const dedupBySn = (base) => {
    const seen = new Set();
    return base.filter(a => !seen.has(a.charger_sn) && seen.add(a.charger_sn));
  };

  const p1Alerts       = dedupBySn(sortedActive.filter(a => fleetP1Sns.has(a.charger_sn)));
  const p2Alerts       = dedupBySn(sortedActive.filter(a => fleetP2Sns.has(a.charger_sn)));
  const p3Alerts       = dedupBySn(sortedActive.filter(a => fleetP3Sns.has(a.charger_sn)));
  const ackedSorted    = [...ackedAlerts].sort(byTimeAlert);
  const resolvedSorted = [...resolvedAlerts].sort(byTimeAlert);

  const filteredAlerts = (() => {
    if      (alertFilter === "ALL")          return sortedActive;
    else if (alertFilter === "ACKNOWLEDGED") return ackedSorted;
    else if (alertFilter === "RESOLVED")     return resolvedSorted;
    else if (alertFilter === "P1")           return p1Alerts;
    else if (alertFilter === "P2")           return p2Alerts;
    else if (alertFilter === "P3")           return p3Alerts;
    else                                     return sortedActive;
  })();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <SignedOut>
        <SignInPage />
      </SignedOut>
      <SignedIn>
        <div className="dp-root" style={{ background: "#F5F7FA", minHeight: "100vh", color: "#111827" }}>
          <style>{`
            @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Inter:wght@400;500;600;700&display=swap');
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: 'Inter', sans-serif; }
            ::-webkit-scrollbar { width: 4px; }
            ::-webkit-scrollbar-track { background: #F3F4F6; }
            ::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 2px; }
            @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
            @keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
            .row-hover:hover { background: #F9FAFB !important; }
            input::placeholder { color: #9CA3AF; }
          `}</style>

          {/* Header */}
          <div className="dp-header" style={{
            borderBottom: "1px solid #E5E7EB", padding: "14px 32px",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            background: "#FFFFFF", boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#0EA5E9", boxShadow: "0 0 6px #0EA5E9aa" }} />
                <span className="dp-brand" style={{ fontSize: 14, fontWeight: 700, color: "#1E3A8A", letterSpacing: "0.06em" }}>DATA PIGEON</span>
              </div>
              <div style={{ display: "flex", gap: 2, padding: "2px", background: "#F3F4F6", borderRadius: 6 }}>
                {[["FLEET","FLEET"], ["ALERTS","ALERTS"]].map(([lbl, val]) => (
                  <button key={val} onClick={() => setTopTab(val)} style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.07em",
                    padding: "5px 14px", borderRadius: 4, cursor: "pointer", border: "none",
                    background: topTab === val ? "#fff" : "transparent",
                    color: topTab === val ? "#1E3A8A" : "#9CA3AF",
                    boxShadow: topTab === val ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                    transition: "all 0.12s",
                  }}>{lbl}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {lastUpdated && <span className="dp-muted" style={{ fontSize: 11, color: "#9CA3AF" }}>Updated {lastUpdated.toLocaleTimeString()}</span>}
              <button
                onClick={() => setDarkMode(d => !d)}
                className={`dp-btn-night${darkMode ? " dp-btn-night-active" : ""}`}
                title={darkMode ? "Switch to light mode" : "Switch to night mode"}
                style={{
                  fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
                  background: darkMode ? "#EEF2FF" : "#F9FAFB",
                  border: `1px solid ${darkMode ? "#A5B4FC" : "#E5E7EB"}`,
                  color: darkMode ? "#4338CA" : "#6B7280",
                }}
              >{darkMode ? "☀️ LIGHT" : "🌙 DARK"}</button>
              <button
                onClick={() => setAutoRefresh(a => !a)}
                className={autoRefresh ? "dp-btn-auto-on" : "dp-btn-ghost"}
                style={{
                  fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer", fontWeight: 500,
                  background: autoRefresh ? "#EFF6FF" : "#F9FAFB",
                  border: `1px solid ${autoRefresh ? "#BFDBFE" : "#E5E7EB"}`,
                  color: autoRefresh ? "#1D4ED8" : "#9CA3AF",
                }}
              >{autoRefresh ? "AUTO ON" : "AUTO OFF"}</button>
              <button
                onClick={fetchFleet}
                className="dp-btn-ghost"
                style={{ fontSize: 11, padding: "5px 12px", borderRadius: 6, cursor: "pointer", background: "#F9FAFB", border: "1px solid #E5E7EB", color: "#6B7280" }}
              >REFRESH</button>
              <div className="dp-divider" style={{ paddingLeft: 8, borderLeft: "1px solid #E5E7EB", display: "flex", alignItems: "center", gap: 8 }}>
                {user && (
                  <span className="dp-user" style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>
                    {user.firstName} {user.lastName}
                  </span>
                )}
                <UserButton />
              </div>
            </div>
          </div>

          {topTab === "ALERTS" && <AlertsTab authHeaders={authHeaders} user={user} darkMode={darkMode} />}

          {topTab === "FLEET" && loading && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
              <div style={{ fontSize: 12, color: "#9CA3AF", letterSpacing: "0.12em" }}>LOADING FLEET DATA...</div>
            </div>
          )}

          {topTab === "FLEET" && error && (
            <div style={{ margin: 32, background: "#FEF2F2", border: "1px solid #FECACA", padding: 20, borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "#EF4444", fontWeight: 600, marginBottom: 6 }}>CONNECTION ERROR</div>
              <div style={{ fontSize: 11, color: "#6B7280" }}>{error}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>Make sure the model server is running on the NUC (<code>DATA_DIR=/home/pigeon uvicorn serve_model:app --host 0.0.0.0 --port 8000</code>).</div>
            </div>
          )}

          {topTab === "FLEET" && fleet && !loading && (
            <div className="dp-themed-area" style={{ padding: "24px 32px", animation: "fadeIn 0.3s ease" }}>

              {/* Stat cards */}
              <div style={{ display: "flex", gap: 10, marginBottom: 24, flexWrap: "wrap", alignItems: "center" }}>
                <StatCard label="Total Chargers"    value={fleet.total_chargers}       sub="click to clear filter"                              accent="#6B7280" tier="ALL"                  activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Active Alerts"     value={fleetP1.length}             sub={<SubLabel tier="P1" />}           accent="#EF4444" tier="P1"                   activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Schedule Today"    value={fleetP2.length}             sub={<SubLabel tier="P2" />}         accent="#F97316" tier="P2"                   activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Muted"             value={mutedChargers.length}       sub="suppressed from alerts"                             accent="#6B7280" tier="MUTED"                activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Derating"          value={deratingChargers.length}    sub="capacity loss detected"                             accent="#7C3AED"  tier="DERATING"             activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Pre-Fault"         value={silentRiskChargers.length}  sub="degradation signals"                                accent="#92400E"  tier="SILENT_RISK"           activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Proactive Maint."  value={proactiveChargers.length}   sub="🔧 14-day forecast"                                 accent="#4F46E5"  tier="PROACTIVE_MAINTENANCE" activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Flapping"          value={flappingChargers.length}    sub="cycling faults"                                     accent="#E74C3C"  tier="FLAPPING"              activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Early Warning"     value={earlyWarning.length}        sub="model early warning"                                accent="#8B5CF6"  tier="EW"                    activeFilter={filter} onFilterChange={setFilter} />
                <StatCard label="Healthy"           value={fleet.P4_monitor}           sub={<SubLabel tier="P4" text="click to filter" />}      accent="#0EA5E9" tier="P4"                   activeFilter={filter} onFilterChange={setFilter} />
                {filter !== "ALL" && (
                  <button onClick={() => setFilter("ALL")} style={{ fontSize: 11, padding: "8px 14px", borderRadius: 6, cursor: "pointer", background: "#1E3A8A", border: "none", color: "#fff", fontWeight: 600 }}>CLEAR FILTER ×</button>
                )}
              </div>

              {/* Map */}
              <div style={{ marginBottom: 20 }}>
                <MapTab ref={mapRef} locations={fleetResults} onChargerSelect={c => setSelected(c)} activeFilter={filter} />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 34%) minmax(520px, 1fr)", gap: 16, alignItems: "start" }}>

                {/* Left — dot grid + early warnings + alert feed + blocklist */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>

                  {/* Fleet dot grid */}
                  <div className="dp-surface" style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                    <div className="dp-divider-light" style={{ padding: "8px 12px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                      <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 600, letterSpacing: "0.08em" }}>
                        FLEET OVERVIEW · {filter === "ALL" ? fleet.total_chargers : displayedDots.length} CHARGERS{filter !== "ALL" ? ` · FILTER: ${filter}` : ""}
                      </span>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                        {Object.entries(TIER_CONFIG).map(([k, v]) => (
                          <span key={k} style={{ fontSize: 9, color: v.color, display: "flex", alignItems: "center", gap: 3 }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: v.color, display: "inline-block" }} />
                            {k}
                          </span>
                        ))}
                        <span style={{ fontSize: 9, color: "#9CA3AF", display: "flex", alignItems: "center", gap: 3 }}>
                          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#9CA3AF", display: "inline-block" }} />
                          MUTED
                        </span>
                      </div>
                    </div>
                    <div style={{ padding: "6px 12px", borderBottom: "1px solid #F3F4F6", display: "flex", alignItems: "center", gap: 6 }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: "#9CA3AF" }}>
                        <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <input className="dp-input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by station ID, site name, or charger serial..."
                        style={{ fontSize: 11, background: "transparent", border: "none", color: "#374151", outline: "none", width: "100%", padding: 0 }} />
                      {search && (
                        <button onClick={() => setSearch("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: 14, lineHeight: 1, padding: 0, flexShrink: 0 }}>×</button>
                      )}
                    </div>
                    <div className="dp-divider" style={{ padding: "6px 12px", borderBottom: "1px solid #F9FAFB", display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                      {[
                        { key: "ALL",                   label: "ALL"       },
                        { key: "P1",                    label: "P1"        },
                        { key: "P2",                    label: "P2"        },
                        { key: "P3",                    label: "P3"        },
                        { key: "P4",                    label: "P4"        },
                        { key: "EW",                    label: "EW"        },
                        { key: "DERATING",              label: "DERAT"     },
                        { key: "SILENT_RISK",           label: "PRE-FAULT" },
                        { key: "PROACTIVE_MAINTENANCE", label: "PROACTIVE" },
                        { key: "FLAPPING",              label: "FLAP"      },
                        { key: "MUTED",                 label: "MUTED"     },
                      ].map(({ key, label }) => (
                        <button key={key} onClick={() => setFilter(key)} className={filter === key ? "dp-filter-btn-active" : "dp-filter-btn"} style={{
                          fontSize: 9, padding: "2px 6px", borderRadius: 4, cursor: "pointer",
                          background: filter === key ? "#1E3A8A" : "#F9FAFB",
                          border: `1px solid ${filter === key ? "#1E3A8A" : "#E5E7EB"}`,
                          color: filter === key ? "#fff" : "#6B7280", fontWeight: filter === key ? 600 : 400,
                        }}>{label}</button>
                      ))}
                    </div>
                    <div style={{ padding: 10, display: "flex", flexWrap: "wrap", gap: 4, maxHeight: 160, overflowY: "auto" }}>
                      {displayedDots.map(c => (
                        <ChargerDot key={c.charger_sn} charger={c} onClick={setSelected}
                          selected={selected?.charger_sn === c.charger_sn} activeFilter={filter} />
                      ))}
                    </div>
                  </div>

                  {/* Early Warning panel */}
                  {earlyWarning.length > 0 && (
                    <div className="dp-surface dp-surface-accent" style={{ background: "#FFFFFF", border: "1px solid #DDD6FE", borderTop: "3px solid #8B5CF6", borderRadius: 8 }}>
                      <div className="dp-divider-light" style={{ padding: "6px 12px", borderBottom: "1px solid #EDE9FE" }}>
                        <span style={{ fontSize: 10, color: "#7C3AED", fontWeight: 700, letterSpacing: "0.08em" }}>
                          EARLY WARNING · {earlyWarning.length}
                        </span>
                      </div>
                      <div style={{ display: "flex", flexWrap: "wrap", maxHeight: 120, overflowY: "auto" }}>
                        {earlyWarning.map(c => (
                          <div key={c.charger_sn} onClick={() => setSelected(c)} className="row-hover"
                            style={{ padding: "6px 10px", borderBottom: "1px solid #F5F3FF", cursor: "pointer", minWidth: 140, flex: "1 1 140px" }}>
                            <div style={{ fontSize: 11, color: "#111827", fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>{c.station_id && c.station_id !== '—' ? c.station_id : c.charger_sn}</div>
                            <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 1 }}>
                              P(fault): {(c.fault_probability * 100).toFixed(1)}% · {c.station_type}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Alert feed */}
                  <div className="dp-surface" style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
                    <div className="dp-divider-light" style={{ padding: "8px 12px", borderBottom: "1px solid #F3F4F6" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                        <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 600, letterSpacing: "0.08em" }}>ALERT FEED</span>
                        <div className="dp-segment" style={{ display: "flex", gap: 2, background: "#F3F4F6", borderRadius: 5, padding: 2 }}>
                          {[["PRIORITY", "Priority"], ["THROUGHPUT", "Throughput"], ["RECOMMENDED", "Recommended"]].map(([val, lbl]) => (
                            <button key={val} onClick={() => setAlertSort(val)} className={alertSort === val ? "dp-segment-btn-active" : ""} style={{
                              fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer", border: "none",
                              fontWeight: alertSort === val ? 700 : 400,
                              background: alertSort === val ? "#fff" : "transparent",
                              color: alertSort === val ? "#1E3A8A" : "#9CA3AF",
                              boxShadow: alertSort === val ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                            }}>{lbl}</button>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid #F3F4F6", flexWrap: "wrap" }}>
                        {[
                          { label: `ALL (${sortedActive.length})`,              value: "ALL" },
                          { label: `P1 (${p1Alerts.length})`,                   value: "P1" },
                          { label: `P2 (${p2Alerts.length})`,                   value: "P2" },
                          { label: `P3 (${p3Alerts.length})`,                   value: "P3" },
                          { label: `DERATING (${deratingChargers.length})`,     value: "DERATING" },
                          { label: `PRE-FAULT (${silentRiskChargers.length})`,  value: "SILENT_RISK" },
                          { label: `PROACTIVE (${proactiveChargers.length})`,   value: "PROACTIVE_MAINTENANCE" },
                          { label: `FLAPPING (${flappingChargers.length})`,     value: "FLAPPING" },
                          { label: `ACK'd (${ackedSorted.length})`,             value: "ACKNOWLEDGED" },
                          { label: `RESOLVED (${resolvedSorted.length})`,       value: "RESOLVED" },
                        ].map(({ label, value }) => (
                          <button key={value} onClick={() => setAlertFilter(value)} style={{
                            fontSize: 9, fontWeight: 700, letterSpacing: "0.07em", padding: "4px 8px",
                            cursor: "pointer", border: "none", background: "none",
                            color: alertFilter === value ? "#1E3A8A" : "#9CA3AF",
                            borderBottom: `2px solid ${alertFilter === value ? "#1E3A8A" : "transparent"}`,
                            marginBottom: -1,
                          }}>{label}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{ maxHeight: 260, overflowY: "auto" }}>
                      {alertFilter === "DERATING" ? (
                        deratingChargers.length === 0 ? (
                          <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>No derated chargers in fleet</div>
                        ) : (alertSort === "THROUGHPUT" ? [...deratingChargers].sort((a, b) => (b.avg_kwh_day_30d ?? 0) - (a.avg_kwh_day_30d ?? 0)) : deratingChargers).map(c => {
                          const { bg, border, color } = DERATING_STYLE[c.derating_severity] || DERATING_STYLE.MODERATE;
                          const pct = c.offered_vs_healthy != null ? Math.round(c.offered_vs_healthy * 100) : null;
                          return (
                            <div key={c.charger_sn} className="row-hover" onClick={() => setSelected(c)} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px", borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}>
                              <div style={{ background: bg, border: `1px solid ${border}`, color, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2, whiteSpace: "nowrap" }}>
                                {c.derating_severity}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 2 }}>
                                  {c.site_name || c.charger_sn}
                                  {c.station_id && c.station_id !== "—" && <span style={{ fontSize: 11, color: "#1E3A8A", fontWeight: 500, marginLeft: 8 }}>{c.station_id}</span>}
                                </div>
                                <div style={{ fontSize: 11, color: "#6B7280" }}>
                                  {pct != null ? `${pct}% capacity` : "reduced capacity"}{c.avg_offered_a > 0 ? ` · ${c.avg_offered_a}A output` : ""}
                                  {c.rpm_alert && <span style={{ color: "#7C3AED", marginLeft: 8 }}>🌀 fan degraded</span>}
                                </div>
                              </div>
                              <div style={{ fontSize: 11, color, fontWeight: 700, flexShrink: 0 }}>{pct != null ? `${pct}%` : "—"}</div>
                            </div>
                          );
                        })
                      ) : alertFilter === "SILENT_RISK" ? (
                        silentRiskChargers.length === 0 ? (
                          <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>No pre-fault chargers detected</div>
                        ) : (alertSort === "THROUGHPUT" ? [...silentRiskChargers].sort((a, b) => (b.avg_kwh_day_30d ?? 0) - (a.avg_kwh_day_30d ?? 0)) : silentRiskChargers).map(c => (
                          <div key={c.charger_sn} className="row-hover" onClick={() => setSelected(c)} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px", borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}>
                            <div style={{ background: "#F5F3FF", border: "1px solid #6D28D9", color: "#6D28D9", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2 }}>
                              SILENT
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A", marginBottom: 1 }}>
                                {c.station_id && c.station_id !== "—" ? c.station_id : c.charger_sn}
                              </div>
                              {c.site_name && <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{c.site_name}</div>}
                              <div style={{ fontSize: 11, color: "#6B7280" }}>
                                Score: {(c.silent_risk_score ?? 0).toFixed(3)} · P(fault): {((c.fault_probability ?? 0) * 100).toFixed(1)}% · {c.station_type}
                                {(c.wo_count_90d ?? 0) > 0 && <span style={{ marginLeft: 8 }}>📋 {c.wo_count_90d} WO</span>}
                                {c.derating_alert && <span style={{ marginLeft: 8, color: "#7C3AED" }}>⚡ derated</span>}
                              </div>
                            </div>
                            <div style={{ fontSize: 11, color: "#6D28D9", fontWeight: 700, flexShrink: 0 }}>
                              {(c.silent_risk_score ?? 0).toFixed(2)}
                            </div>
                          </div>
                        ))
                      ) : alertFilter === "PROACTIVE_MAINTENANCE" ? (
                        proactiveChargers.length === 0 ? (
                          <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>No chargers in proactive maintenance queue</div>
                        ) : [...proactiveChargers].sort((a, b) => alertSort === "THROUGHPUT" ? (b.avg_kwh_day_30d ?? 0) - (a.avg_kwh_day_30d ?? 0) : (b.bilstm_parts_prob ?? 0) - (a.bilstm_parts_prob ?? 0)).map(c => (
                          <div key={c.charger_sn} className="row-hover" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px", borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}
                            onClick={() => setSelected(c)}>
                            <div style={{ background: "#EEF2FF", border: "1px solid #4F46E5", color: "#4F46E5", fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2 }}>🔧</div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 1, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A" }}>
                                  {c.station_id && c.station_id !== "—" ? c.station_id : c.charger_sn}
                                </span>
                                <button onClick={e => { e.stopPropagation(); setScheduleTarget(c); }} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, border: "1px solid #4F46E5", background: "#EEF2FF", color: "#4F46E5", fontWeight: 700, cursor: "pointer" }}>SCHEDULE</button>
                              </div>
                              {c.site_name && <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{c.site_name}</div>}
                              <div style={{ fontSize: 11, color: "#6B7280" }}>
                                v6 fault: {Math.round((c.fault_probability ?? 0) * 100)}%
                                {c.bilstm_fault_prob != null && <span> · BiLSTM fault: {Math.round(c.bilstm_fault_prob * 100)}%</span>}
                                {c.days_since_last_wo < 999 && <span> · Last WO: {c.days_since_last_wo}d ago</span>}
                                {c.derating_alert && <span style={{ color: "#7C3AED" }}> · ⚡ derated</span>}
                              </div>
                            </div>
                            <div style={{ fontSize: 12, color: "#4F46E5", fontWeight: 700, flexShrink: 0 }}>
                              {Math.round((c.bilstm_parts_prob ?? 0) * 100)}%
                            </div>
                          </div>
                        ))
                      ) : alertFilter === "FLAPPING" ? (
                        flappingChargers.length === 0 ? (
                          <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>No flapping chargers detected</div>
                        ) : flappingChargers.map(c => {
                          const sev = (c.flap_severity || "active").toLowerCase();
                          const fs  = FLAP_STYLE[sev] || FLAP_STYLE.active;
                          return (
                            <div key={c.charger_sn} className="row-hover" onClick={() => setSelected(c)} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px", borderBottom: "1px solid #F9FAFB", cursor: "pointer" }}>
                              <div style={{ background: fs.bg, border: `1px solid ${fs.border}`, color: fs.color, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2, whiteSpace: "nowrap" }}>
                                ⚡ {(c.flap_severity || "ACTIVE").toUpperCase()}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A", marginBottom: 1 }}>
                                  {c.station_id && c.station_id !== "—" ? c.station_id : c.charger_sn}
                                </div>
                                {c.site_name && <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>{c.site_name}</div>}
                                <div style={{ fontSize: 11, color: "#6B7280" }}>
                                  {c.flap_count_24hr != null && <span>{c.flap_count_24hr} cycles · </span>}
                                  {c.flap_avg_dwell_sec != null && c.flap_avg_dwell_sec > 0 && <span>{Math.round(c.flap_avg_dwell_sec)}s avg dwell · </span>}
                                  v6: {Math.round((c.fault_probability ?? 0) * 100)}%
                                </div>
                                {c.flap_dominant_error && <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>Error: {c.flap_dominant_error}</div>}
                              </div>
                              <div style={{ fontSize: 11, color: fs.color, fontWeight: 700, flexShrink: 0 }}>{c.station_type}</div>
                            </div>
                          );
                        })
                      ) : filteredAlerts.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", fontSize: 12, color: "#9CA3AF" }}>No {alertFilter.toLowerCase()} alerts</div>
                      ) : filteredAlerts.map(al => {
                        const tc        = getTier(al.priority_tier);
                        const fleetItem = fleetResults.find(r => r.charger_sn === al.charger_sn);
                        const isAcked    = al.status === "acknowledged";
                        const isResolved = al.status === "resolved";
                        return (
                          <div key={al.id} className="row-hover" style={{
                            display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px",
                            borderBottom: "1px solid #F9FAFB",
                            opacity: isResolved ? 0.5 : 1,
                          }}>
                            <div style={{ background: tc.bg, border: `1px solid ${tc.color}`, color: tc.color, fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4, flexShrink: 0, marginTop: 2 }}>
                              {al.priority_tier?.slice(0, 2) || "?"}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => fleetItem && setSelected(fleetItem)}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A" }}>
                                  {fleetItem?.station_id && fleetItem.station_id !== "—"
                                    ? fleetItem.station_id
                                    : (al.station_id && al.station_id !== "—" ? al.station_id : al.charger_sn)}
                                </span>
                                <span style={{ fontSize: 10, color: "#9CA3AF", marginLeft: "auto", textAlign: "right", lineHeight: 1.4 }}>
                                  {fmtAlertDate(al.triggered_at)}<br />{timeAgo(al.triggered_at)}
                                </span>
                              </div>
                              {(fleetItem?.site_name || al.site_name) && (
                                <div style={{ fontSize: 12, color: "#374151", marginBottom: 2 }}>
                                  {fleetItem?.site_name || al.site_name}
                                </div>
                              )}
                              <div style={{ fontSize: 11, color: "#9CA3AF" }}>
                                <span style={{ fontFamily: "'DM Mono', monospace" }}>{al.charger_sn}</span>
                                {fleetItem?.station_type && <span> · {fleetItem.station_type}</span>}
                                {" · "}{((al.fault_probability || 0) * 100).toFixed(1)}% fault prob
                              </div>
                              {al.error_description && (
                                <div style={{ fontSize: 11, color: "#F97316", marginTop: 2 }}>
                                  ⚠ {fleetItem?.error_info?.impact || "H"} · {al.error_description}
                                </div>
                              )}
                              {isAcked && al.acknowledged_by && (
                                <div style={{ fontSize: 10, color: "#6B7280", marginTop: 2 }}>
                                  Acked by {al.acknowledged_by} · {timeAgo(al.acknowledged_at)}
                                  {al.notes && <span style={{ color: "#9CA3AF" }}> — {al.notes}</span>}
                                </div>
                              )}
                            </div>
                            {!isAcked && !isResolved && (
                              <div style={{ display: "flex", gap: 5, flexShrink: 0, marginTop: 2 }}>
                                <button onClick={() => setAckTarget(al)} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer", border: "1px solid #BFDBFE", background: "#EFF6FF", color: "#1D4ED8", fontWeight: 600 }}>✓ ACK</button>
                                <button onClick={() => setMuteTarget({ charger_sn: al.charger_sn })} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer", border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#6B7280", fontWeight: 600 }}>MUTE</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Muted chargers panel */}
                  <div className="dp-surface" style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8 }}>
                    <button onClick={() => setBlocklistOpen(o => !o)} style={{
                      width: "100%", padding: "8px 12px", display: "flex", justifyContent: "space-between",
                      alignItems: "center", background: "none", border: "none", cursor: "pointer", textAlign: "left",
                    }}>
                      <span style={{ fontSize: 10, fontWeight: 600, color: "#6B7280", letterSpacing: "0.08em" }}>
                        MUTED CHARGERS · {blocklist.length}
                      </span>
                      <span style={{ fontSize: 12, color: "#9CA3AF" }}>{blocklistOpen ? "▲" : "▼"}</span>
                    </button>
                    {blocklistOpen && (
                      <div style={{ borderTop: "1px solid #F3F4F6" }}>
                        {blocklist.length === 0 ? (
                          <div style={{ padding: "16px", fontSize: 12, color: "#9CA3AF", textAlign: "center" }}>No muted chargers</div>
                        ) : blocklist.map(b => (
                          <div key={b.charger_sn} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 16px", borderBottom: "1px solid #F9FAFB" }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", fontFamily: "'DM Mono', monospace" }}>{b.charger_sn}</div>
                              <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>{b.reason}</div>
                              <div style={{ fontSize: 10, color: "#D1D5DB", marginTop: 1 }}>by {b.blocked_by} · {timeAgo(b.blocked_at)}</div>
                            </div>
                            <button
                              onClick={async () => {
                                const headers = await authHeaders();
                                await fetch(`${API}/blocklist/${b.charger_sn}`, { method: "DELETE", headers });
                                fetchBlocklist();
                              }}
                              style={{ fontSize: 10, padding: "3px 8px", borderRadius: 4, cursor: "pointer", border: "1px solid #E5E7EB", background: "#F9FAFB", color: "#6B7280", fontWeight: 600, flexShrink: 0, marginTop: 2 }}
                            >Unmute</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

                {/* Right — detail panel */}
                <div style={{ position: "sticky", top: 24, minWidth: 0 }}>
                  {selected ? (
                    <DetailPanel
                      charger={selected}
                      onClose={() => { setSelected(null); mapRef.current?.clearPin(); }}
                      onShowOnMap={showSelectedOnMap}
                    />
                  ) : (
                    <div className="dp-surface" style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8, padding: 32, textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: "#D1D5DB", letterSpacing: "0.1em", marginBottom: 8 }}>SELECT A CHARGER</div>
                      <div style={{ fontSize: 11, color: "#E5E7EB" }}>Click any dot or row to inspect</div>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer — model freshness */}
              <div className="dp-divider" style={{ marginTop: 24, borderTop: "1px solid #E5E7EB", paddingTop: 14, display: "flex", flexWrap: "wrap", gap: 20, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#9CA3AF", letterSpacing: "0.07em" }}>MODELS ACTIVE: XGBoost v6 · BiLSTM v2 · Silent Risk</span>
                {fleet?.features_computed_at && (
                  <span style={{ fontSize: 10, color: "#9CA3AF" }}>
                    Last fleet score: <span style={{ color: "#6B7280", fontWeight: 500 }}>{timeAgo(fleet.features_computed_at)}</span> (XGBoost)
                    {bilstmScoredAt && <span> · <span style={{ color: "#6B7280", fontWeight: 500 }}>{timeAgo(bilstmScoredAt)}</span> (BiLSTM)</span>}
                  </span>
                )}
                <span style={{ fontSize: 10, color: "#9CA3AF" }}>
                  {fleet?.total_chargers} chargers scored
                  {chargersWithoutBilstm > 0 && <span style={{ color: "#D97706", fontWeight: 500 }}> · {chargersWithoutBilstm} awaiting BiLSTM run</span>}
                </span>
              </div>

            </div>
          )}
        </div>

        <MuteModal
          target={muteTarget}
          onClose={() => setMuteTarget(null)}
          onConfirm={async (reason) => {
            const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
            await fetch(`${API}/blocklist`, {
              method: "POST", headers,
              body: JSON.stringify({ charger_sn: muteTarget.charger_sn, reason, blocked_by: user?.primaryEmailAddress?.emailAddress || "dashboard" }),
            });
            setMuteTarget(null);
            fetchBlocklist();
            fetchAlerts();
          }}
        />
        <AckModal
          target={ackTarget}
          onClose={() => setAckTarget(null)}
          onConfirm={async (notes) => {
            const alertId = ackTarget.id;
            const by = user?.primaryEmailAddress?.emailAddress || "dashboard";
            const prev = alerts;
            setAlerts(a => a.map(x => x.id === alertId
              ? { ...x, status: "acknowledged", acknowledged_by: by, acknowledged_at: new Date().toISOString() }
              : x
            ));
            setAckTarget(null);
            try {
              const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
              const res = await fetch(`${API}/alerts/${alertId}/acknowledge`, {
                method: "POST", headers,
                body: JSON.stringify({ acknowledged_by: by, notes }),
              });
              if (!res.ok) throw new Error();
              showToast("Alert acknowledged — removed from active feed");
            } catch {
              setAlerts(prev);
              showToast("Failed to acknowledge alert — please retry");
            }
          }}
        />
        <ScheduleWOModal
          target={scheduleTarget}
          onClose={() => setScheduleTarget(null)}
          onConfirm={async (description) => {
            const c = scheduleTarget;
            setScheduleTarget(null);
            try {
              const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
              const res = await fetch(`${API}/charger/${c.charger_sn}/work-orders`, {
                method: "POST", headers,
                body: JSON.stringify({
                  title: "Proactive maintenance — predicted parts replacement",
                  category: "Replace Parts (new)",
                  description,
                  status: "Scheduled",
                }),
              });
              if (!res.ok) throw new Error();
              showToast(`Work order created for ${c.station_id && c.station_id !== "—" ? c.station_id : c.charger_sn}`);
            } catch {
              showToast("Failed to create work order — please retry");
            }
          }}
        />
        {toast && (
          <div style={{
            position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
            background: "#1E3A8A", color: "#fff", padding: "10px 20px",
            borderRadius: 8, fontSize: 12, zIndex: 9999,
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)", pointerEvents: "none",
          }}>{toast}</div>
        )}
      </SignedIn>
    </>
  );
}
